import React, { useState } from 'react';

/**
 * Simple test component for the essay-ideas API
 * Add this to your admin dashboard to quickly test if the API is working
 */
export default function EssayAPITester() {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const testAPI = async () => {
    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const response = await fetch('/api/essay-ideas/generate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          interviewType: 'hidden_competencies',
          studentName: 'Alex',
          responses: {
            go_to_person: "I'm the person everyone asks about their phone problems. Started when I helped my grandma set up her new iPhone, and now the whole family texts me when something's not working.",
            taught_adult: "I taught my dad how to use Discord so he could join my gaming sessions. Took three sessions but now he's better at screen sharing than I am.",
            taught_myself: "Learned video editing from YouTube because I wanted to make better clips for our robotics team. Our team's social media engagement went up 300%."
          },
          context: {
            personality: 'Analytical problem-solver',
            assessmentCompleted: true
          }
        })
      });

      if (!response.ok) {
        const text = await response.text();
        throw new Error(`HTTP ${response.status}: ${text}`);
      }

      const data = await response.json();
      setResult(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-lg border border-stone-200 p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-bold text-slate-900">Essay API Tester</h3>
        <button
          onClick={testAPI}
          disabled={loading}
          className="px-4 py-2 bg-azure-600 text-white rounded-lg hover:bg-azure-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? 'Testing...' : 'Test API'}
        </button>
      </div>

      {loading && (
        <div className="text-center py-8">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-azure-600"></div>
          <p className="mt-4 text-slate-600">Calling API...</p>
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <h4 className="font-bold text-red-900 mb-2">❌ Error</h4>
          <pre className="text-sm text-red-800 whitespace-pre-wrap">{error}</pre>
        </div>
      )}

      {result && (
        <div className="space-y-4">
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <h4 className="font-bold text-green-900 mb-2">✅ Success!</h4>
          </div>

          {result.magneticHook && (
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
              <h4 className="font-semibold text-slate-900 mb-2">🧲 Magnetic Hook</h4>
              <p className="text-slate-700">{result.magneticHook}</p>
            </div>
          )}

          {result.surpriseInsight && (
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
              <h4 className="font-semibold text-slate-900 mb-2">💡 Surprise Insight</h4>
              <p className="text-slate-700">{result.surpriseInsight}</p>
            </div>
          )}

          {result.essayTopics && result.essayTopics.length > 0 && (
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
              <h4 className="font-semibold text-slate-900 mb-3">📝 Essay Topics ({result.essayTopics.length})</h4>
              <div className="space-y-3">
                {result.essayTopics.map((topic, index) => (
                  <div key={index} className="border-l-4 border-azure-500 pl-3">
                    <h5 className="font-semibold text-slate-900">{index + 1}. {topic.title}</h5>
                    <p className="text-sm text-slate-600 mt-1">{topic.story}</p>
                    {topic.essayAngle && (
                      <p className="text-sm text-azure-700 mt-1 italic">Angle: {topic.essayAngle}</p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {result.hiddenStrengths && result.hiddenStrengths.length > 0 && (
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
              <h4 className="font-semibold text-slate-900 mb-2">💪 Hidden Strengths</h4>
              <ul className="list-disc list-inside space-y-1 text-slate-700">
                {result.hiddenStrengths.map((strength, i) => (
                  <li key={i} className="text-sm">{strength}</li>
                ))}
              </ul>
            </div>
          )}

          <details className="bg-slate-50 border border-slate-200 rounded-lg p-4">
            <summary className="font-semibold text-slate-900 cursor-pointer">
              View Raw JSON
            </summary>
            <pre className="mt-2 text-xs text-slate-700 overflow-x-auto">
              {JSON.stringify(result, null, 2)}
            </pre>
          </details>
        </div>
      )}

      <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded text-sm text-blue-900">
        <p><strong>Test Data:</strong> This sends 3 sample responses from "Alex" for the Hidden Competencies interview.</p>
      </div>
    </div>
  );
}
